/**
 * VideoEditor — create/edit dialog for a Learn video.
 *
 * Supports two source modes:
 *   - "url"    → paste a YouTube / Vimeo / direct .mp4 link
 *   - "upload" → POST to /api/learn/upload; captures storage_path
 *
 * On save it POSTs (new) or PUTs (edit) the metadata record.
 */
import { useEffect, useRef, useState } from "react";
import { X, Loader2, Link as LinkIcon, Upload, Youtube } from "lucide-react";
import { toast } from "sonner";
import {
  createVideo,
  updateVideo,
  uploadFile,
  youTubeThumb,
  isYouTubeUrl,
} from "@/lib/learn/api";

const emptyDraft = () => ({
  title: "",
  description: "",
  category: "",
  external_url: "",
  storage_path: "",
  thumbnail_url: "",
  duration_seconds: "",
  order: 0,
  published: true,
  language: "en",
  poster_time_seconds: 2,
});

export default function VideoEditor({ initial, categories, defaultLanguage = "en", onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [source, setSource] = useState(() =>
    initial?.storage_path ? "upload" : "url",
  );
  const [draft, setDraft] = useState(() => ({
    ...emptyDraft(),
    language: defaultLanguage || "en",
    ...initial,
    duration_seconds:
      initial?.duration_seconds != null ? String(initial.duration_seconds) : "",
    order: initial?.order ?? 0,
    published: initial?.published ?? true,
    language: initial?.language || defaultLanguage || "en",
    poster_time_seconds:
      initial?.poster_time_seconds != null ? String(initial.poster_time_seconds) : "2",
  }));
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadedName, setUploadedName] = useState(
    initial?.storage_path ? initial.storage_path.split("/").pop() : "",
  );
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setUploadPct(0);
    try {
      const res = await uploadFile(f, { onProgress: setUploadPct });
      setField("storage_path", res.storage_path);
      setField("external_url", "");
      setUploadedName(f.name);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const canSave = () => {
    if (!draft.title.trim() || !draft.category) return false;
    if (source === "url" && !draft.external_url.trim()) return false;
    if (source === "upload" && !draft.storage_path) return false;
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave() || saving) return;
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        description: draft.description.trim(),
        category: draft.category,
        thumbnail_url:
          draft.thumbnail_url?.trim() ||
          (source === "url" && isYouTubeUrl(draft.external_url)
            ? youTubeThumb(draft.external_url)
            : null),
        duration_seconds: draft.duration_seconds
          ? Number(draft.duration_seconds)
          : null,
        order: Number(draft.order) || 0,
        published: !!draft.published,
        language: draft.language === "fr" ? "fr" : "en",
        poster_time_seconds:
          draft.poster_time_seconds === "" || draft.poster_time_seconds == null
            ? 2
            : Math.max(0, Math.min(3600, Number(draft.poster_time_seconds) || 0)),
      };
      if (source === "upload") {
        payload.storage_path = draft.storage_path;
        payload.external_url = null;
      } else {
        payload.external_url = draft.external_url.trim();
        payload.storage_path = null;
      }
      const saved = isEdit
        ? await updateVideo(initial.id, payload)
        : await createVideo(payload);
      toast.success(isEdit ? "Video updated" : "Video added");
      onSaved?.(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="learn-video-editor"
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl p-6 sm:p-8 my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 h-9 w-9 rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100 flex items-center justify-center transition"
          aria-label="Close"
          data-testid="learn-editor-close"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-2xl font-bold font-heading text-brand-navy">
          {isEdit ? "Edit video" : "Add a video"}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Fields marked with * are required.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Title *
            </label>
            <input
              type="text"
              value={draft.title}
              onChange={(e) => setField("title", e.target.value)}
              className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal"
              data-testid="learn-editor-title"
              maxLength={200}
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Language *
            </label>
            <div className="mt-1.5 inline-flex rounded-full border border-slate-200 p-1 bg-slate-50" data-testid="learn-editor-language">
              {[
                { key: "en", label: "English" },
                { key: "fr", label: "Français" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setField("language", key)}
                  className={`px-4 py-1.5 text-sm font-semibold rounded-full transition ${
                    draft.language === key
                      ? "bg-white text-brand-navy shadow-sm"
                      : "text-slate-500 hover:text-brand-navy"
                  }`}
                  data-testid={`learn-editor-language-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-slate-400">
              Chosen language decides which /learn library the video appears in.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Category *
            </label>
            <select
              value={draft.category}
              onChange={(e) => setField("category", e.target.value)}
              className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
              data-testid="learn-editor-category"
              required
            >
              <option value="">Choose a category…</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {(draft.language === "fr" && c.label_fr) ? c.label_fr : c.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Description
            </label>
            <textarea
              value={draft.description}
              onChange={(e) => setField("description", e.target.value)}
              rows={3}
              className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal"
              data-testid="learn-editor-description"
            />
          </div>

          {/* Source picker */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              Video source *
            </p>
            <div className="inline-flex rounded-full border border-slate-200 p-1 bg-slate-50">
              {[
                { key: "url",    label: "Link", icon: LinkIcon },
                { key: "upload", label: "Upload", icon: Upload },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSource(key)}
                  className={`px-4 py-1.5 text-sm font-semibold rounded-full flex items-center gap-1.5 transition ${
                    source === key
                      ? "bg-white text-brand-navy shadow-sm"
                      : "text-slate-500 hover:text-brand-navy"
                  }`}
                  data-testid={`learn-editor-source-${key}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {source === "url" && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                YouTube / Vimeo / MP4 URL *
              </label>
              <input
                type="url"
                placeholder="https://youtu.be/…  or  https://vimeo.com/…"
                value={draft.external_url}
                onChange={(e) => setField("external_url", e.target.value)}
                className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal"
                data-testid="learn-editor-url"
              />
              {isYouTubeUrl(draft.external_url) && (
                <p className="mt-1 text-xs text-slate-500 flex items-center gap-1">
                  <Youtube className="h-3 w-3" /> Detected as YouTube — thumbnail will be auto-fetched.
                </p>
              )}
            </div>
          )}

          {source === "upload" && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Upload MP4 / MOV / WebM (max 600 MB) *
              </label>
              <div className="mt-1.5 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-slate-200 bg-white text-brand-navy font-semibold hover:bg-slate-50 disabled:opacity-50 transition"
                  data-testid="learn-editor-upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading {uploadPct}%
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      {draft.storage_path ? "Replace file" : "Choose file"}
                    </>
                  )}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v"
                  className="hidden"
                  onChange={onPickFile}
                  data-testid="learn-editor-file-input"
                />
                {uploadedName && (
                  <span className="text-sm text-slate-500 truncate">
                    {uploadedName}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Duration (seconds)
              </label>
              <input
                type="number"
                min="0"
                value={draft.duration_seconds}
                onChange={(e) => setField("duration_seconds", e.target.value)}
                className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
                data-testid="learn-editor-duration"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Order
              </label>
              <input
                type="number"
                value={draft.order}
                onChange={(e) => setField("order", e.target.value)}
                className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
                data-testid="learn-editor-order"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Still frame at (seconds)
            </label>
            <input
              type="number"
              min="0"
              max="3600"
              step="0.1"
              value={draft.poster_time_seconds}
              onChange={(e) => setField("poster_time_seconds", e.target.value)}
              placeholder="2"
              className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
              data-testid="learn-editor-poster-time"
            />
            <p className="mt-1 text-xs text-slate-400">
              Which frame the card preview grabs before the video plays. Default: 2s. Ignored if you set a custom Thumbnail URL below.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Thumbnail URL (optional)
            </label>
            <input
              type="url"
              value={draft.thumbnail_url || ""}
              onChange={(e) => setField("thumbnail_url", e.target.value)}
              placeholder="Leave blank to auto-generate for YouTube links"
              className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
              data-testid="learn-editor-thumb"
            />
          </div>

          <label className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              checked={!!draft.published}
              onChange={(e) => setField("published", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-teal focus:ring-brand-teal/40"
              data-testid="learn-editor-published"
            />
            <span className="text-sm text-slate-600">
              Publish immediately (visible to the public)
            </span>
          </label>

          <div className="pt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 h-11 rounded-full text-sm font-semibold text-slate-600 hover:text-brand-navy hover:bg-slate-100 transition"
              data-testid="learn-editor-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSave() || saving || uploading}
              className="px-6 h-11 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white font-semibold disabled:opacity-50 flex items-center gap-2 transition"
              data-testid="learn-editor-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isEdit ? "Save changes" : "Add video"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
